import { ReactNode, useState } from "react";
import classnames from "classnames";
import { useNavigate } from "react-router-dom";
import { makeTemplateComponent, ScrollWrapper } from "../Elements/Elements.tsx";
import Loading from "../Elements/Loading/Loading.tsx";
import { IconChevronDown, IconChevronUp } from "../Icon/Icon.tsx";
import useQueryParams from "../Fetch/useQueryParams.tsx";
import Pagination, {
  setOffsetToSearchParams,
  usePaginationState,
} from "./Pagination.tsx";
import styles from "./Table.module.css";

const TableComponent = makeTemplateComponent("table", styles.table);
const THeadComponent = makeTemplateComponent("thead", styles.thead);
const ThComponent = makeTemplateComponent("th", styles.th);
const TBodyComponent = makeTemplateComponent("tbody", styles.tbody);
const TrComponent = makeTemplateComponent("tr", styles.tr);
const TdComponent = makeTemplateComponent("td", styles.td);

export const useSortBy = () => {
  const { sortBy: init = {} } = useQueryParams();
  const [sortBy, setSortBy] = useState(init);

  return [sortBy, setSortBy];
};

const Table = ({
  data,
  columns = [],
  idName = "id",
  showPagination = false,
  paginationState,
  count,
  sortByState,
  isLoading = false,
  className,
}: TableType) => {
  const getIdForKey = (row, append = "") => row[idName] + "" + append;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sortBy, setSortBy] = sortByState ?? useSortBy();
  const [pagination, setPaginationTemp] =
    paginationState ?? usePaginationState(); // eslint-disable-line react-hooks/rules-of-hooks
  let setPagination = setPaginationTemp;
  if (!paginationState) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const navigate = useNavigate();
    setPagination = ({ offset, limit }) => {
      setPaginationTemp({ limit, offset });
      navigate({
        ...location,
        search: setOffsetToSearchParams(location.search, limit, offset),
      });
    };
  }

  const makeRow = (row) => {
    return (
      <TrComponent key={getIdForKey(row)}>
        {columns.map((column, j) => makeCell(column, row, getIdForKey(row, j)))}
      </TrComponent>
    );
  };

  const makeCell = (column, row, key) => {
    const { Cell, accessor, className, width } = column;
    let res = null;
    let acc = null;
    if (typeof accessor === "function") {
      acc = accessor(row);
    } else {
      acc = row[accessor];
    }
    if (Cell) {
      res = <Cell {...{ value: acc, original: row }} />;
    } else {
      res = acc;
    }
    return (
      <TdComponent key={key} className={className} style={{ width: width }}>
        {res}
      </TdComponent>
    );
  };

  const makeHeader = (column, key) => {
    const { Header, sortField, classNameHeader, width } = column;
    return (
      <ThComponent
        key={key}
        className={classNameHeader}
        style={{ width: width }}
      >
        <div className={styles.headerWrapper}>
          <div className={styles.sortWrapper}>
            <div
              onClick={() => {
                if (!sortField) {
                  return;
                }

                // Si on appuie une nouvelle fois sur la flèche, on réinitialise le tableau
                if (sortBy[sortField] === "1") {
                  setSortBy({});
                } else {
                  const asc = [];
                  asc[sortField] = "1";
                  setSortBy({ ...asc });
                }
              }}
              className={classnames({
                [styles.sort]: sortField,
                [styles.noSort]: !sortField,
                [styles.sortActive]: sortBy[sortField] === "1",
              })}
            >
              <IconChevronUp />
            </div>
            <div
              onClick={() => {
                if (!sortField) {
                  return;
                }
                // Si on appuie une nouvelle fois sur la flèche, on réinitialise le tableau
                if (sortBy[sortField] === "-1") {
                  setSortBy({});
                } else {
                  const desc = [];
                  desc[sortField] = "-1";
                  setSortBy({ ...desc });
                }
              }}
              className={classnames({
                [styles.sort]: sortField,
                [styles.noSort]: !sortField,
                [styles.sortActive]: sortBy[sortField] === "-1",
              })}
            >
              <IconChevronDown />
            </div>
          </div>
          <div className={styles.headerTitle}>{Header}</div>
        </div>
      </ThComponent>
    );
  };

  return (
    <ScrollWrapper>
      <div className={styles.tableWrapper}>
        <TableComponent className={className}>
          <THeadComponent>
            {columns.map((column, i) => makeHeader(column, i))}
          </THeadComponent>
          {!isLoading && data?.length > 0 && (
            <TBodyComponent>{data?.map(makeRow)}</TBodyComponent>
          )}
        </TableComponent>
        {showPagination && data?.length > 0 && (
          <Pagination
            isLoading={isLoading}
            count={count}
            paginationState={[pagination, setPagination]}
            dataLength={data?.length}
          />
        )}
        {isLoading && (
          <div className={styles.noData}>
            <Loading />
          </div>
        )}
        {!isLoading && data?.length === 0 && (
          <div className={styles.noData}>Aucune donnée</div>
        )}
      </div>
    </ScrollWrapper>
  );
};

type TableType = {
  data: any[];
  isLoading?: boolean;
  showPagination?: boolean;
  count?: number;
  paginationState: [
    { offset: number; limit: number },
    ({ limit, offset }: { limit: number; offset: number }) => void,
  ];
  className?: string;
  columns: {
    Header?: string | ReactNode;
    accessor: string | ReactNode;
    sortField?: () => string | string;
    Cell: ReactNode;
    className?: string;
    classNameHeader?: string;
    width?: string;
  }[];
  idName: string;
  sortByState?: any[];
  trClassName?: string;
  nomPage?: ReactNode;
};

export default Table;
